/**
 * 全屏视图浮层「传送门」。
 *
 * 背景：时间表全屏用的是浏览器原生 Fullscreen API（TimeTable.toggleFullscreen →
 * requestFullscreen）。进入全屏后 .tablebox 会被提升到浏览器的 **top layer**，
 * 它连同后代一起盖在页面一切内容之上；而挂在 document.body 下的浮层
 * （Element 的 MessageBox / Message / Notification，以及页面上其它 fixed 浮窗）
 * 不属于这条全屏链路，无论 z-index 写多高都会被全屏视图盖住 ——
 * 表现就是「全屏里点 × 删除，确认弹窗不出现」「删除成功提示看不见」
 * 「悬停时间块没有评论气泡」「定时提醒弹窗没反应」。
 *
 * 解法：浮层创建 / 显示后，把它的 DOM 节点搬进 document.fullscreenElement
 * （= 全屏中的 .tablebox），让它作为全屏元素的后代一起进入 top layer。要点：
 * - 这些浮层都是 position:fixed + 视口坐标定位，换父级不影响位置；
 *   .tablebox 自身没有 transform / filter，不会变成 fixed 元素的包含块，
 *   overflow 也不会裁剪 fixed 后代（同款结论见 FriendTablePanel 的面板注释）。
 * - 退出全屏后元素留在 .tablebox 里也无妨：.tablebox 不建层叠上下文
 *   （见其 CSS 里那段 isolation 警告注释），z-index 依旧在页面根层生效；
 *   下次 Element 重新 show 时会把它 append 回 body，自动归位。
 * - 非全屏时全部为 no-op，普通页面行为零变化。
 */

/** 当前处于原生全屏的元素（时间表全屏时就是 .tablebox；没有则 null） */
export function getFullscreenElement() {
  if (typeof document === 'undefined') return null
  return document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.msFullscreenElement ||
    null
}

/** 当前是否处于原生全屏 */
export function isFullscreenActive() {
  return !!getFullscreenElement()
}

/**
 * 把一个浮层 DOM 节点搬进全屏元素。
 * @returns {boolean} 是否真的搬了（非全屏 / 无节点 / 已在其中 → false）
 */
export function portalToFullscreen(el) {
  const fs = getFullscreenElement()
  if (!fs || !el || !el.parentNode || el.parentNode === fs) return false
  fs.appendChild(el)
  return true
}

/** Element 浮层实例（$message / $notify 的返回值）→ 搬它的根节点 */
export function portalInstanceToFullscreen(instance) {
  const el = instance && (instance.$el || instance.dom)
  return el ? portalToFullscreen(el) : false
}

/** 把 document.body 下匹配选择器的元素搬进全屏元素（MessageBox 是 body 下的单例，用它兜底） */
export function portalSelectorToFullscreen(selector) {
  if (!isFullscreenActive()) return false
  const el = document.querySelector(selector)
  return el ? portalToFullscreen(el) : false
}

/**
 * 包装「返回浮层实例」的方法（Message / Notification）：照原样调用，
 * 拿回实例后立刻把浮层搬进全屏元素。静态方法（.success / .error …）一并复制包装。
 */
export function wrapFullscreenAware(raw) {
  const wrapped = function(...args) {
    const inst = raw(...args)
    portalInstanceToFullscreen(inst)
    return inst
  }
  Object.keys(raw).forEach(name => {
    if (typeof raw[name] !== 'function') return
    wrapped[name] = function(...args) {
      const inst = raw[name](...args)
      portalInstanceToFullscreen(inst)
      return inst
    }
  })
  return wrapped
}
